import React, { useEffect, useState, useMemo, memo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  FaCheckCircle,
  FaTree,
  FaMapMarkerAlt,
  FaCalendarPlus,
  FaCalendarAlt,
  FaClock,
  FaPagelines,
  FaUserAlt,
  FaSatellite
} from "react-icons/fa";
import api from "../../axios/api";

const verifiedIcon = new L.Icon({
  iconUrl: "/images/map_pin_icon.png",
  iconSize: [36, 48],
  iconAnchor: [18, 48],
  popupAnchor: [0, -45],
});

const unverifiedIcon = new L.Icon({
  iconUrl: "/images/map_pin_icon.png",
  iconSize: [36, 48],
  iconAnchor: [18, 48],
  popupAnchor: [0, -45],
});

const getSpeciesLabel = (tree) => {
  if (!tree) return 'Unknown Species';
  const commonName = (tree.specie_common_name || tree.common_name || tree.species_common_name || '').trim();
  const scientificName = (tree.tree_species_label || tree.speciesLabel || tree.species_label || tree.label || '').trim();
  if (commonName && scientificName && commonName.toLowerCase() !== scientificName.toLowerCase()) {
    return `${commonName} / ${scientificName}`;
  }
  return commonName || scientificName || tree.treeName || (tree.tree_species ? `Species-${tree.tree_species}` : 'Unknown Species');
};

// High-performance memoized marker with on-demand popup
const TreeMarker = memo(({ tree, isSelected, onSelect, onClose }) => {
  const lat = Number(tree.lat);
  const lng = Number(tree.lng);
  const icon = tree.verified || tree.isVerified ? verifiedIcon : unverifiedIcon;

  return (
    <Marker
      position={[lat, lng]}
      icon={icon}
      eventHandlers={{
        click: () => onSelect(tree),
      }}
    >
      {isSelected && (
        <Popup onClose={onClose} autoPan={true}>
          <div style={{
            display: 'flex',
            minWidth: '400px',
            maxWidth: '600px',
            gap: '4px',
            padding: '0'
          }}>
            <div style={{ width: '40%' }}>
              <a
                target="_blank"
                className="search-more-info-btn"
                href={`/plantprofile/${tree.id}`}
                rel="noreferrer"
                title="More Info"
              >
                <img
                  src={tree.treeImageUrl}
                  alt={tree.imageName}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'scale-down',
                    borderRadius: '4px'
                  }}
                />
              </a>
            </div>
            <div style={{
              width: '40%',
              fontSize: '8px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
              }}>
                <a
                  target="_blank"
                  className="search-more-info-btn"
                  href={`/plantprofile/${tree.id}`}
                  rel="noreferrer"
                  title="More Info"
                >
                  <p style={{
                    margin: 0,
                    lineHeight: '1'
                  }}>{tree.treeName}</p>
                </a>
                {tree.isVerified ? (
                  <FaCheckCircle style={{
                    color: '#16a34a',
                    fontSize: '12px'
                  }} />
                ) : (<FaCheckCircle style={{
                  color: 'yellow',
                  fontSize: '12px'
                }} />)}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '1px' }}>
                <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="TreeId">
                  <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}><FaTree style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span>{' '}
                  {tree.tree_id}
                </p>
                <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Uploaded By">
                  <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}><FaUserAlt style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span>{' '}
                  {tree.userName}
                </p>
                <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Uploaded On">
                  <span style={{ fontWeight: 600, color: 'black' }}><FaCalendarPlus style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span>{' '}
                  {tree.createdAt && !isNaN(new Date(tree.createdAt)) ? new Date(tree.createdAt).toLocaleDateString() : 'N/A'}
                </p>
                <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Planted On">
                  <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}><FaCalendarAlt style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span>{' '}
                  {tree.plantedOn && !isNaN(new Date(tree.plantedOn)) ? new Date(tree.plantedOn).toLocaleDateString() : 'N/A'}
                </p>
                <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Species">
                  <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}><FaPagelines style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span>{' '}
                  {getSpeciesLabel(tree)}
                </p>
                <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Age">
                  <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}><FaClock style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span>{' '}
                  {tree.age}
                </p>

                <p style={{
                  margin: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  wordBreak: 'break-word',
                  fontSize: '10px',
                  lineHeight: '1',
                  padding: '0'
                }} title="Location">
                  <span style={{ fontWeight: 600, color: 'black' }}><FaMapMarkerAlt style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span> {tree.address}
                </p>
              </div>
            </div>
          </div>
        </Popup>
      )}
    </Marker>
  );
});

const TreeMap = () => {
  const [trees, setTrees] = useState([]);
  const [selectedTreeId, setSelectedTreeId] = useState(null);
  const [isSatellite, setIsSatellite] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const response = await api.get('/api/upload/all-images', { skipLoader: true });
        if (isMounted && response.data?.images) {
          setTrees(response.data.images);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Filter out invalid coordinates to prevent Leaflet crash and optimize performance
  const validTrees = useMemo(() => {
    if (!Array.isArray(trees)) return [];
    return trees.filter(
      (tree) =>
        tree &&
        tree.lat !== null &&
        tree.lng !== null &&
        !isNaN(Number(tree.lat)) &&
        !isNaN(Number(tree.lng)) &&
        Number(tree.lat) >= -90 &&
        Number(tree.lat) <= 90 &&
        Number(tree.lng) >= -180 &&
        Number(tree.lng) <= 180
    );
  }, [trees]);

  const position = [20.5937, 78.9629];

  return (
    <div style={{ position: "relative", width: "100%", height: "100vh", zIndex: 1 }}>
      {/* Floating Satellite Toggle Button */}
      <div
        className="leaflet-top leaflet-right"
        style={{ marginTop: "10px", marginRight: "10px", zIndex: 1000, pointerEvents: "auto" }}
      >
        <div className="leaflet-control">
          <button
            onClick={() => setIsSatellite((prev) => !prev)}
            type="button"
            className="rounded-full shadow-lg flex items-center justify-center font-bold bg-green-500 text-white p-2 hover:bg-green-600 transition-colors cursor-pointer border-0"
            title="Toggle Satellite / Street View"
          >
            <FaSatellite size={30} />
          </button>
        </div>
      </div>

      <MapContainer
        center={position}
        zoom={4}
        preferCanvas={true}
        style={{ height: "100vh", width: "100%" }}
      >
        <TileLayer
          url={
            isSatellite
              ? "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
              : "https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
          }
          maxZoom={20}
          updateWhenIdle={true}
          updateWhenZooming={false}
          keepBuffer={6}
        />

        <MarkerClusterGroup
          chunkedLoading={true}
          chunkInterval={50}
          chunkDelay={10}
          maxClusterRadius={50}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          removeOutsideVisibleBounds={true}
          disableClusteringAtZoom={18}
          animate={true}
        >
          {validTrees.map((tree) => {
            const treeKey = tree.id || `${tree.lat}-${tree.lng}-${tree.tree_id}`;
            return (
              <TreeMarker
                key={treeKey}
                tree={tree}
                isSelected={selectedTreeId === treeKey}
                onSelect={() => setSelectedTreeId(treeKey)}
                onClose={() => setSelectedTreeId(null)}
              />
            );
          })}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  );
};

export default TreeMap;